/**
 * Page 1 of Create trigger drawer
 * Configure trigger event type
 */
import {Select} from '@wandb/weave/components/Form/Select';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {components, type OptionProps} from 'react-select';
import {useShallow} from 'zustand/react/shallow';

import {useIsModelsOnly} from '../../../../pages/ProjectPage/ProjectAccessContext';
import {Analytics} from '../../../../services/analytics';
import {Update} from '../../../../util/filters';
import {type FilterKey, type ValueFilter} from '../../../../util/filterTypes';
import {Banner, BannerFlexWrapper, BannerTextWrapper} from '../../../Banner';
import {SemanticLoader as Loader} from '../../../utility/InstrumentedLoader';
import {SelectEntity} from '../../Queues/SelectEntity';
import {
  ArtifactAliasFilterCreator,
  ArtifactLinkFilterCreator,
  ArtifactOrCollectionTagFilterCreator,
  CreateArtifactFilterCreator,
  type EventConfigurationProps,
} from '../TriggerEventConfiguration';
import {
  EventTriggeringConditionType,
  isKnownEventType,
  type KnownEventType,
  makeTriggerOptionForSelect,
  makeTriggerOptionWithDisabledState,
  type TriggerOptionType,
} from '../TriggerEventTypes';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {EventStoreActions, useEventStore} from './EventStore';
import {HubScopeRow} from './HubScopeRow';
import {RunMetricChangeSection} from './RunMetricChangeSection';
import {RunMetricThresholdSection} from './RunMetricThresholdSection';
import {RunMetricZscoreSection} from './RunMetricZscoreSection';
import {RunStateSection} from './RunStateSection';
import {SeparatedChunk} from './shared';
import {SwitchToBasicButton} from './SwitchToBasicButton';
import {EntryPoint} from './types';
import {WeaveMetricThresholdSection} from './WeaveMetricAlertSection';

// we don't include this in EventTypeToMetadata since it introduces
// circular dependencies with CreateTriggerLogic.ts/TriggerEventTypes.ts
export const EventTypeToConfigComponent: Record<
  KnownEventType,
  React.FC<EventConfigurationProps> | undefined
> = {
  [EventTriggeringConditionType.AddArtifactAlias]: ArtifactAliasFilterCreator,
  [EventTriggeringConditionType.CreateArtifact]: CreateArtifactFilterCreator,
  [EventTriggeringConditionType.LinkModel]: ArtifactLinkFilterCreator,
  // UnlinkArtifact mirrors LinkModel: same scope-only setup, no extra fields.
  [EventTriggeringConditionType.UnlinkArtifact]: ArtifactLinkFilterCreator,
  [EventTriggeringConditionType.UpdateArtifactAlias]:
    ArtifactAliasFilterCreator,
  // All four tag events (artifact + collection, add + remove) share the
  // same tag-regex + optional collection picker UI.
  [EventTriggeringConditionType.AddArtifactTag]:
    ArtifactOrCollectionTagFilterCreator,
  [EventTriggeringConditionType.RemoveArtifactTag]:
    ArtifactOrCollectionTagFilterCreator,
  [EventTriggeringConditionType.AddCollectionTag]:
    ArtifactOrCollectionTagFilterCreator,
  [EventTriggeringConditionType.RemoveCollectionTag]:
    ArtifactOrCollectionTagFilterCreator,
  [EventTriggeringConditionType.RunMetric]: RunMetricThresholdSection,
  [EventTriggeringConditionType.RunMetricChange]: RunMetricChangeSection,
  [EventTriggeringConditionType.RunState]: RunStateSection,
  [EventTriggeringConditionType.RunMetricZscore]: RunMetricZscoreSection,
  [EventTriggeringConditionType.WeaveMetricThreshold]:
    WeaveMetricThresholdSection,
};

// Custom Option component that shows a tooltip for disabled options
const TriggerOption = (props: OptionProps<TriggerOptionType | null, false>) => {
  const {data, isDisabled} = props;
  const content = <components.Option {...props} />;

  if (isDisabled && data?.disabledReason) {
    return (
      <span title={data.disabledReason}>
        <div>{content}</div>
      </span>
    );
  }
  return content;
};

export const DrawerPageEventConfiguration = () => {
  const eventConfig = useEventStore(useShallow(s => s));

  // isModelsOnly defaults to false in non-project contexts (e.g. registries) where no
  // Weave event types are in entryPointToEventOptions, so this flag has no effect.
  // Revisit if needed.
  const isModelsOnly = useIsModelsOnly();

  React.useEffect(() => {
    if (eventConfig.entryPoint) {
      Analytics.track('Automation Creation', {
        type: 'page_view',
        page: 'event_configuration',
        entryPoint: eventConfig.entryPoint,
      });
    }
    // this is just for tracking arrival analytics, so we don't need to
    // re-run this on every event type change. Included entrypoint check
    // since sometimes it wasn't getting initialized in workspaces
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [eventConfig.entryPoint]);

  const eventType = eventConfig.eventType;
  const EventConfigComponent =
    eventType && isKnownEventType(eventType)
      ? EventTypeToConfigComponent[eventType]
      : null;

  // In the hub create flow, defer the Event selector until the user picks a scope:
  // the available event types depend on scope kind, and run-* events are scope-specific
  // (e.g. valid only for Project scopes). Other entry points always have a scope.
  const shouldShowEventSelector =
    eventConfig.entryPoint !== EntryPoint.AUTOMATIONS_HUB ||
    eventConfig.automationsHubScopeKind != null;

  if (eventConfig.loading) {
    return <Loader active name="create-trigger-drawer-event-config" />;
  }

  return (
    <>
      <S.PageLabelRow>Step 1 of 3 - Event configuration</S.PageLabelRow>
      {eventConfig.entryPoint === EntryPoint.AUTOMATIONS_HUB && <HubScopeRow />}
      {eventConfig.entryPoint === EntryPoint.REGISTRY && (
        <Tailwind>
          <div className="pt-16">
            <Banner variant="info">
              <BannerFlexWrapper>
                <Icon name="lightbulb-info" />
                <BannerTextWrapper>
                  <div>
                    <span className="font-semibold">
                      Registry level automation.
                    </span>{' '}
                    Automations are applied to all collections in the registry,
                    including those added in the future.
                  </div>
                </BannerTextWrapper>
              </BannerFlexWrapper>
            </Banner>
          </div>
        </Tailwind>
      )}
      {eventConfig.getShowSelectTeam() && (
        <>
          {eventConfig.entryPoint === EntryPoint.TEAM_MODEL_REGISTRY && (
            <Tailwind>
              <div className="pt-16">
                <Banner variant="info">
                  <BannerFlexWrapper>
                    <Icon name="lightbulb-info" />
                    <BannerTextWrapper>
                      <div>
                        Automations created here are applied to all collections
                        in a selected team's model registry, including future
                        collections.
                      </div>
                    </BannerTextWrapper>
                  </BannerFlexWrapper>
                </Banner>
              </div>
            </Tailwind>
          )}

          <S.LabelRow>
            <S.Label>Team</S.Label>
          </S.LabelRow>
          <SelectEntity
            includeAll={false}
            value={eventConfig.entityName ?? ''}
            onSelectEntity={en => {
              // We can't check purely on "is entityName valid", since that
              // would then prevent the user from changing the entityName
              // once one is picked. unknownEntityName captures that the
              // caller of CreateTriggerDrawer didn't provide an entityName,
              // so we should allow the user to pick & change teams.
              if (eventConfig.allowEntityNameChange) {
                EventStoreActions.setEntityNameAsync(en);
              }
            }}
            includePersonalEntity={false}
          />
          {eventConfig.allowEntityNameChange && (
            <Tailwind>
              <div className="text-moon-500 dark:text-wb-text-tertiary">
                The automation will be applied to the selected team's model
                registry.
              </div>
            </Tailwind>
          )}
        </>
      )}

      {shouldShowEventSelector && (
        <>
          <S.LabelRow>
            <S.Label>Event</S.Label>
          </S.LabelRow>
          <Select<TriggerOptionType | null>
            options={eventConfig
              .getAvailableConditionTypes()
              .map(et => makeTriggerOptionWithDisabledState(et, isModelsOnly))}
            isOptionDisabled={option => !!option?.isDisabled}
            value={
              eventType && isKnownEventType(eventType)
                ? makeTriggerOptionForSelect(eventType)
                : null
            }
            placeholder="Select an event type..."
            onChange={(d: TriggerOptionType | null) => {
              const eventType = d?.value as EventTriggeringConditionType;
              EventStoreActions.setEventType(eventType);

              Analytics.track('Automation Creation', {
                type: 'event_selected',
                eventType,
                entryPoint: eventConfig.entryPoint,
                entityName: eventConfig.entityName,
              });
            }}
            components={{Option: TriggerOption}}
          />

          {EventConfigComponent != null && (
            <div>
              <EventConfigComponent
                filters={eventConfig.filters.filters[0]}
                setFilter={(index: number, f: ValueFilter<FilterKey>) => {
                  EventStoreActions.setFilters(
                    Update.setFilter(eventConfig.filters, [0, index], f)
                  );
                }}
                pushFilter={(newFilter: ValueFilter<FilterKey>) => {
                  EventStoreActions.setFilters(
                    Update.groupPush(eventConfig.filters, [0], newFilter)
                  );
                }}
                deleteFilter={(index: number) =>
                  EventStoreActions.setFilters(
                    Update.groupRemove(eventConfig.filters, [0], index)
                  )
                }
                setTriggerScopeType={scopeType =>
                  EventStoreActions.setScopeType(scopeType)
                }
                setTriggerScopeID={id => EventStoreActions.setScopeID(id)}
                triggerScopeID={eventConfig.scopeID}
                triggerScopeType={eventConfig.scopeType}
                artifactCollectionID={
                  eventConfig.initProps.artifactCollectionID
                }
                setTriggerArtifactCollectionName={artifactCollectionName =>
                  EventStoreActions.setArtifactCollectionName(
                    artifactCollectionName
                  )
                }
              />
              <SeparatedChunk>
                <SwitchToBasicButton />
              </SeparatedChunk>
            </div>
          )}
        </>
      )}
    </>
  );
};
